import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Printer, RefreshCw, TriangleAlert } from "lucide-react";
import { Modal } from "@/components/Modals";
import { Button } from "@/components/ui/button";
import { Segmented } from "@/components/ui/segmented";
import { a4Html, fitPage, pickColumns } from "@/components/reports/reportPrint";

/**
 * Print a report on the thermal roll or on A4 (owner, 2026-10-26).
 *
 * The preview is the print, not a picture of it: thermal shows the characters
 * the server built for that printer's paper width, and A4 shows the very page
 * the browser prints. Columns are chosen here because neither a 48-character
 * roll nor a portrait page holds every report as it is on screen.
 *
 * "Save as my default" keeps the current choices on this person's login, per
 * report (owner, 2026-10-26), so the next print of that report opens set up.
 */

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api/admin/reports/print`;
const auth = token => ({ headers: token ? { Authorization: `Bearer ${token}` } : {} });

const errorText = failure => {
  const detail = failure?.response?.data?.detail;
  if (typeof detail === "string" && detail) return detail;
  if (Array.isArray(detail) && detail.length) return detail.map(d => d?.msg || String(d)).join("; ");
  return failure?.message || "Could not reach the server";
};

const printedAt = () =>
  new Date().toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true });

export default function ReportPrintDialog({ doc, slug, token, restaurant, onClose }) {
  const allKeys = useMemo(() => doc.columns.map(column => column.key), [doc]);
  const [format, setFormat] = useState("thermal");
  // Columns per format: what fits A4 landscape is rarely what fits a roll.
  const [columnsBy, setColumnsBy] = useState(() => ({ thermal: allKeys, a4: allKeys }));
  const chosen = columnsBy[format];
  const setChosen = update =>
    setColumnsBy(current => ({ ...current, [format]: typeof update === "function" ? update(current[format]) : update }));
  const [printerId, setPrinterId] = useState(null);
  const [layout, setLayout] = useState("auto");
  const [orientation, setOrientation] = useState("portrait");
  const [size, setSize] = useState("normal");
  // undefined while loading, null when this person has none for this report.
  const [savedDefault, setSavedDefault] = useState(undefined);
  const [savingDefault, setSavingDefault] = useState(false);
  // Set by any click or change in the controls: a default that arrives after the
  // person has started choosing must not undo what they just did.
  const touched = useRef(false);
  const markTouched = () => { touched.current = true; };

  const [printers, setPrinters] = useState(null);
  const [printersError, setPrintersError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const frame = useRef(null);
  const pagePane = useRef(null);
  const [paneWidth, setPaneWidth] = useState(0);

  // The A4 page is drawn at its real size and zoomed to the pane, so the preview
  // is the whole width of the page, not a corner of it.
  useEffect(() => {
    const pane = pagePane.current;
    if (!pane || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(([entry]) => setPaneWidth(entry.contentRect.width));
    observer.observe(pane);
    return () => observer.disconnect();
  }, [format]);

  const switchFormat = setFormat;

  // A column the report no longer has is dropped; none left means all of them.
  const keptColumns = keys => {
    const kept = (keys || []).filter(key => allKeys.includes(key));
    return kept.length ? kept : allKeys;
  };

  const applyDefault = saved => {
    setFormat(saved.format);
    setColumnsBy({ thermal: keptColumns(saved.columns?.thermal), a4: keptColumns(saved.columns?.a4) });
    if (saved.printer_id != null) setPrinterId(saved.printer_id);
    setLayout(saved.layout);
    setOrientation(saved.orientation);
    setSize(saved.size);
  };

  // This person's saved default for this report, applied as the dialog opens.
  useEffect(() => {
    let live = true;
    axios
      .get(`${API}/defaults/${slug}`, auth(token))
      .then(response => {
        if (!live) return;
        const saved = response.data?.default || null;
        setSavedDefault(saved);
        if (saved && !touched.current) applyDefault(saved);
      })
      // No default to load is not a reason to stop printing: start from the plain choices.
      .catch(() => { if (live) setSavedDefault(null); });
    return () => { live = false; };
  }, [slug, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const picked = useMemo(() => pickColumns(doc, chosen), [doc, chosen]);

  useEffect(() => {
    if (format !== "thermal" || printers) return;
    axios
      .get(`${API}/printers`, auth(token))
      .then(response => {
        const list = Array.isArray(response.data) ? response.data : [];
        setPrinters(list);
        setPrintersError(list.length ? null : "No printer is set up. Add one in Admin → Printers.");
      })
      .catch(failure => setPrintersError(errorText(failure)));
  }, [format, printers, token, attempt]);

  // The saved printer may since have been removed or switched off: fall back to the first.
  useEffect(() => {
    if (printers?.length && !printers.some(p => p.id === printerId)) setPrinterId(printers[0].id);
  }, [printers, printerId]);

  const thermalBody = useMemo(() => ({
    printer_id: printerId,
    title: picked.title,
    subtitle: picked.subtitle || null,
    columns: picked.columns.map(column => ({ label: column.label, numeric: column.numeric })),
    rows: picked.rows,
    totals: picked.totals,
    extra_lines: picked.extraLines || [],
    layout,
  }), [picked, printerId, layout]);

  // Thermal preview: asked of the server, which renders for that printer's width.
  useEffect(() => {
    if (format !== "thermal" || !printerId || !picked.columns.length) {
      setPreviewing(false);
      return undefined;
    }
    let live = true;
    setPreviewing(true);
    const timer = setTimeout(() => {
      axios
        .post(`${API}/thermal`, { ...thermalBody, preview: true }, auth(token))
        .then(response => { if (live) { setPreview(response.data); setPreviewError(null); } })
        .catch(failure => { if (live) { setPreview(null); setPreviewError(errorText(failure)); } })
        .finally(() => { if (live) setPreviewing(false); });
    }, 250);
    return () => { live = false; clearTimeout(timer); };
  }, [format, printerId, thermalBody, token, picked.columns.length, attempt]);

  const html = useMemo(
    () => (format === "a4" ? a4Html(picked, { orientation, size, restaurant, printedAt: printedAt() }) : ""),
    [format, picked, orientation, size, restaurant]
  );
  // Measured on the rendered page each time it loads (see fitPage).
  const [fit, setFit] = useState(null);
  const measurePage = () => setFit(fitPage(frame.current?.contentDocument, { fit: size === "fit" }));
  // "Fit page" leaves the HTML as it is (the shrink is applied to the loaded
  // page), so the frame does not reload: measure again when the choice changes.
  useEffect(() => {
    if (format === "a4") measurePage();
  }, [size]); // eslint-disable-line react-hooks/exhaustive-deps

  const current = useMemo(() => ({
    format, printer_id: printerId, layout, orientation, size,
    columns: { thermal: columnsBy.thermal, a4: columnsBy.a4 },
  }), [format, printerId, layout, orientation, size, columnsBy]);
  // Compared field by field in a fixed order, the saved columns as this report has them.
  // The printer only matters on the roll: on A4 it is whatever the list loaded first.
  const sameChoices = (a, b) =>
    ["format", "layout", "orientation", "size"].every(key => a[key] === b[key]) &&
    (a.format === "a4" || a.printer_id === b.printer_id) &&
    ["thermal", "a4"].every(f => keptColumns(a.columns?.[f]).join("|") === keptColumns(b.columns?.[f]).join("|"));
  const isSavedDefault = Boolean(savedDefault) && sameChoices(current, savedDefault);

  const saveDefault = async () => {
    setSavingDefault(true);
    try {
      const { data } = await axios.put(`${API}/defaults/${slug}`, current, auth(token));
      setSavedDefault(data.default);
      toast.success(`Saved as your default for ${doc.title}`);
    } catch (failure) {
      const unavailable = failure?.response?.status >= 500 && !failure?.response?.data?.detail;
      toast.error(unavailable
        ? "Default not saved: saved defaults are not set up on this server yet"
        : `Default not saved: ${errorText(failure)}`);
    } finally {
      setSavingDefault(false);
    }
  };

  const clearDefault = async () => {
    setSavingDefault(true);
    try {
      await axios.delete(`${API}/defaults/${slug}`, auth(token));
      setSavedDefault(null);
      toast.success(`Your default for ${doc.title} is cleared`);
    } catch (failure) {
      toast.error(`Default not cleared: ${errorText(failure)}`);
    } finally {
      setSavingDefault(false);
    }
  };

  const toggle = key =>
    setChosen(current => {
      if (current.includes(key)) return current.length > 1 ? current.filter(k => k !== key) : current;
      return allKeys.filter(k => k === key || current.includes(k));
    });

  const printThermal = async () => {
    setPrinting(true);
    try {
      const { data } = await axios.post(`${API}/thermal`, { ...thermalBody, preview: false }, auth(token));
      const name = printers?.find(p => p.id === printerId)?.name || "the printer";
      if (data.queued_offline) toast.warning(`${name} is not answering — the report will print when it is back`);
      else toast.success(`Sent to ${name}`);
      onClose();
    } catch (failure) {
      toast.error(`Not printed: ${errorText(failure)}`);
    } finally {
      setPrinting(false);
    }
  };

  const printA4 = () => {
    const win = frame.current?.contentWindow;
    if (!win) return;
    win.focus();
    win.print();
  };

  const pageWidthPx = (orientation === "portrait" ? 794 : 1123) + 40;
  const zoom = paneWidth ? Math.min(1, paneWidth / pageWidthPx) : 1;

  return (
    <Modal
      title="Print report"
      description={`${doc.title}${doc.subtitle ? ` · ${doc.subtitle}` : ""}`}
      onClose={onClose}
      testId="report-print-modal"
      size="wide"
      footer={(
        <>
          <div className="mr-auto flex items-center gap-snug">
            <Button
              variant="outline"
              onClick={saveDefault}
              disabled={savingDefault || savedDefault === undefined || isSavedDefault}
              data-testid="report-print-save-default-button"
            >
              {isSavedDefault ? <><Check />Your default</> : "Save as my default"}
            </Button>
            {savedDefault && (
              <button type="button" className="text-meta text-primary hover:underline disabled:opacity-50" onClick={clearDefault}
                disabled={savingDefault} data-testid="report-print-clear-default-button">
                Clear default
              </button>
            )}
          </div>
          <Button variant="outline" onClick={onClose} data-testid="report-print-cancel-button">Cancel</Button>
          <Button
            variant="action"
            onClick={format === "thermal" ? printThermal : printA4}
            disabled={printing || !picked.columns.length || (format === "thermal" && (!printerId || !preview || previewing))}
            data-testid="report-print-submit-button"
          >
            <Printer />
            {format === "thermal" ? (printing ? "Sending…" : "Print on thermal") : "Print on A4"}
          </Button>
        </>
      )}
    >
      <div className="report-print-grid" data-density="compact" onClickCapture={markTouched} onChangeCapture={markTouched}>
        <div className="flex flex-col gap-loose min-w-0">
          <div className="flex flex-col gap-tight">
            <span className="text-meta font-medium">Paper</span>
            <Segmented
              value={format}
              onValueChange={switchFormat}
              options={[{ value: "thermal", label: "Thermal roll" }, { value: "a4", label: "A4" }]}
              itemTestIdPrefix="report-print-format"
              aria-label="Paper"
            />
          </div>

          {format === "thermal" ? (
            <>
              <label className="flex flex-col gap-tight text-meta font-medium">
                Printer
                <select
                  className="h-8 rounded-md border bg-background px-2 text-body"
                  value={printerId ?? ""}
                  onChange={event => setPrinterId(Number(event.target.value))}
                  disabled={!printers?.length}
                  data-testid="report-print-printer-select"
                >
                  {(printers || []).map(p => (
                    <option key={p.id} value={p.id}>{p.name} · {p.column_width} characters</option>
                  ))}
                </select>
              </label>
              <div className="flex flex-col gap-tight">
                <span className="text-meta font-medium">Layout</span>
                <Segmented
                  value={layout}
                  onValueChange={setLayout}
                  options={[{ value: "auto", label: "Auto" }, { value: "table", label: "Table" }, { value: "list", label: "List" }]}
                  itemTestIdPrefix="report-print-layout"
                  aria-label="Layout"
                />
                <span className="text-micro text-muted-foreground">
                  Auto prints a table when it fits the roll, otherwise one block per row.
                </span>
              </div>
            </>
          ) : (
            <>
              <div className="flex flex-col gap-tight">
                <span className="text-meta font-medium">Orientation</span>
                <Segmented
                  value={orientation}
                  onValueChange={setOrientation}
                  options={[{ value: "portrait", label: "Portrait" }, { value: "landscape", label: "Landscape" }]}
                  itemTestIdPrefix="report-print-orientation"
                  aria-label="Orientation"
                />
              </div>
              <div className="flex flex-col gap-tight">
                <span className="text-meta font-medium">Text size</span>
                <Segmented
                  value={size}
                  onValueChange={setSize}
                  options={[{ value: "normal", label: "Normal" }, { value: "small", label: "Small" }, { value: "fit", label: "Fit page" }]}
                  itemTestIdPrefix="report-print-size"
                  aria-label="Text size"
                />
              </div>
            </>
          )}

          <fieldset className="flex flex-col gap-tight min-w-0" data-testid="report-print-columns">
            <legend className="text-meta font-medium mb-tight">
              Columns · {chosen.length} of {allKeys.length}
            </legend>
            <div className="flex gap-snug text-micro">
              <button type="button" className="text-primary hover:underline" onClick={() => setChosen(allKeys)} data-testid="report-print-columns-all">All</button>
              <button type="button" className="text-primary hover:underline" onClick={() => setChosen(allKeys.slice(0, 1))} data-testid="report-print-columns-first">Only {doc.columns[0]?.label}</button>
            </div>
            <div className="max-h-60 overflow-y-auto border rounded-md divide-y">
              {doc.columns.map(column => (
                <label key={column.key} className="flex items-center gap-snug px-2 py-1.5 text-body cursor-pointer hover:bg-muted/50">
                  <input
                    type="checkbox"
                    checked={chosen.includes(column.key)}
                    onChange={() => toggle(column.key)}
                    data-testid={`report-print-column-${column.key}`}
                  />
                  <span className="truncate">{column.label}</span>
                </label>
              ))}
            </div>
          </fieldset>
        </div>

        <div className="flex flex-col gap-tight min-w-0" data-testid="report-print-preview">
          <span className="text-meta font-medium">Preview — exactly what prints</span>
          {format === "thermal" ? (
            <div className="report-print-roll-pane">
              {printersError ? (
                <PreviewProblem text={printersError} onRetry={() => { setPrinters(null); setPrintersError(null); setAttempt(n => n + 1); }} />
              ) : previewError ? (
                <PreviewProblem text={previewError} onRetry={() => setAttempt(n => n + 1)} />
              ) : preview ? (
                <>
                  {preview.layout === "list" && layout !== "list" && (
                    <p className="text-micro text-muted-foreground mb-tight" data-testid="report-print-list-note">
                      {layout === "table"
                        ? "These columns cannot sit side by side on this roll, so it prints one block per row."
                        : `These columns do not fit ${preview.width} characters as a table, so it prints one block per row. Untick columns for a table.`}
                    </p>
                  )}
                  {preview.unprintable > 0 && (
                    <p className="text-micro text-muted-foreground mb-tight" data-testid="report-print-unprintable-note">
                      {preview.unprintable} character(s) — Hindi or other non-English text — print as "?" on this printer.
                    </p>
                  )}
                  <pre className="report-print-roll" style={{ width: `${preview.width + 2}ch` }} data-testid="report-print-thermal-text" aria-busy={previewing}>
                    {preview.preview}
                  </pre>
                  {preview.preview_cut && (
                    <p className="text-micro text-muted-foreground mt-tight" data-testid="report-print-preview-cut">
                      Showing the start of the slip; all {preview.preview_lines} lines print.
                    </p>
                  )}
                </>
              ) : (
                <p className="text-meta text-muted-foreground p-base">{printers ? "Building the preview…" : "Loading printers…"}</p>
              )}
            </div>
          ) : (
            <>
              {fit && size === "fit" && fit.zoom < 1 && !fit.overflow && (
                <p className="text-meta text-muted-foreground" role="status" data-testid="report-print-a4-zoom">
                  Shrunk to {Math.round(fit.zoom * 100)}% so every column fits.
                </p>
              )}
              {fit?.overflow && (
                <p className="flex items-start gap-tight text-meta text-foreground bg-warning-subtle border border-warning/40 rounded-md px-2 py-1.5" role="status" data-testid="report-print-a4-overflow">
                  <TriangleAlert className="w-4 h-4 shrink-0 mt-hair" />
                  <span>
                    {size === "fit"
                      ? "Even at the smallest readable size these columns are wider than the page, so the right edge would be cut off."
                      : "These columns are wider than the page, so the right edge would be cut off."}
                    {` Try ${[orientation === "portrait" && "landscape", size !== "fit" && "Fit page", "unticking some columns"].filter(Boolean).join(" or ")}.`}
                  </span>
                </p>
              )}
              <div className="report-print-page-pane" ref={pagePane}>
                <iframe
                  ref={frame}
                  title="A4 print preview"
                  srcDoc={html}
                  onLoad={measurePage}
                  style={{ width: `${pageWidthPx}px`, height: `${Math.round((window.innerHeight * 0.62) / zoom)}px`, zoom }}
                  data-testid="report-print-a4-frame"
                />
              </div>
              <span className="text-micro text-muted-foreground">Pages break where the browser puts them; the header row repeats on every page.</span>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}

function PreviewProblem({ text, onRetry }) {
  return (
    <div className="flex flex-col items-start gap-tight p-base" role="alert" data-testid="report-print-preview-error">
      <span className="flex items-center gap-tight text-meta text-danger"><TriangleAlert className="w-4 h-4" />{text}</span>
      <Button variant="outline" size="sm" onClick={onRetry}><RefreshCw />Retry</Button>
    </div>
  );
}
